/**
 * Creative Studio · Step 3 — Asset Kit gallery.
 * Surface tabs · primary preview · copy panel · "why this works" · carousel gallery.
 */
import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';
import {
  Lightbulb, DownloadSimple, Copy as CopyIcon, BookmarkSimple,
} from '@phosphor-icons/react';
import { CreativePreview, RENDER_W } from './CreativePreview';

const API = process.env.REACT_APP_BACKEND_URL;
const LIME = '#c8f060';

const Pill = ({ children, color = LIME }) => (
  <span className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em]"
    style={{ background: `${color}1f`, color, border: `1px solid ${color}33` }}>
    {children}
  </span>
);

const SurfaceTab = ({ id, label, active, onClick }) => (
  <button onClick={() => onClick(id)} data-testid={`surface-tab-${id}`}
    className="px-4 py-2 rounded-xl text-[12px] font-semibold transition"
    style={{
      background: active ? 'rgba(200,240,96,0.1)' : 'transparent',
      color: active ? LIME : 'rgba(245,240,232,0.55)',
      border: active ? '1px solid rgba(200,240,96,0.25)' : '1px solid rgba(255,255,255,0.06)',
    }}>
    {label}
  </button>
);

const SURFACES = [
  { id: 'flyer',        label: 'Flyer' },
  { id: 'ig_post',      label: 'IG Post' },
  { id: 'ig_story',     label: 'IG Story' },
  { id: 'linkedin',     label: 'LinkedIn' },
  { id: 'ad_version',   label: 'Ad' },
  { id: 'email_header', label: 'Email Header' },
];

const CarouselGallery = ({ slides, direction }) => {
  if (!slides?.length) return null;
  return (
    <div className="mt-6">
      <p className="text-[10px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: 'rgba(245,240,232,0.5)' }}>Premium carousel</p>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollSnapType: 'x mandatory' }}>
        {slides.map((slide) => (
          <div key={slide.n} className="flex-shrink-0" style={{ scrollSnapAlign: 'start', width: 270 }}
            data-testid={`carousel-slide-${slide.n}`}>
            <div className="aspect-square rounded-2xl overflow-hidden mb-2"
              style={{ background: direction.visual_spec?.background || '#0a0a0a' }}>
              <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: RENDER_W * 2, height: RENDER_W * 2 }}>
                <CreativePreview surface="ig_post" direction={direction} copy={null} slide={slide} />
              </div>
            </div>
            <p className="text-[10px] tracking-[0.18em] uppercase" style={{ color: 'rgba(245,240,232,0.4)' }}>
              Slide {slide.n} · {slide.role}
              {slide.illustration_url && <span className="ml-1" style={{ color: LIME }}> · illustrated</span>}
            </p>
            <p className="text-[12px] mt-0.5 line-clamp-2" style={{ color: 'rgba(245,240,232,0.78)' }}>{slide.headline}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const CopyPanel = ({ copy }) => {
  const copyTo = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied.');
  };
  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: 'rgba(245,240,232,0.5)' }}>Headline</p>
          <button onClick={() => copyTo(copy.headline)} className="text-[10px]" style={{ color: LIME }}>
            <CopyIcon size={10} className="inline" /> copy
          </button>
        </div>
        <p className="text-2xl leading-snug" style={{ fontFamily: "'Instrument Serif', serif", color: '#fff' }}>{copy.headline}</p>
        <p className="text-sm mt-1.5 italic" style={{ color: 'rgba(245,240,232,0.65)' }}>{copy.subheadline}</p>
      </div>

      <div>
        <p className="text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: 'rgba(245,240,232,0.5)' }}>Hook variations</p>
        <div className="space-y-1.5">
          {(copy.hook_variations || []).map((h, i) => (
            <div key={i} className="flex items-center justify-between text-[13px] p-2 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.02)', color: 'rgba(245,240,232,0.85)' }}
              data-testid={`hook-var-${i}`}>
              <span className="flex-1">{h}</span>
              <button onClick={() => copyTo(h)} className="text-[10px] ml-2" style={{ color: 'rgba(245,240,232,0.4)' }}>copy</button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: 'rgba(245,240,232,0.5)' }}>Supporting copy</p>
        <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.8)' }}>{copy.supporting_copy}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <span className="px-3.5 py-1.5 rounded-full text-[11px] font-bold" style={{ background: LIME, color: '#0a0a0a' }}>
          {copy.cta_primary}
        </span>
        {copy.cta_secondary && (
          <span className="px-3.5 py-1.5 rounded-full text-[11px]" style={{ color: 'rgba(245,240,232,0.65)', border: '1px solid rgba(255,255,255,0.1)' }}>
            {copy.cta_secondary}
          </span>
        )}
      </div>
    </div>
  );
};

export default function AssetStep({ project, onReset }) {
  const ak = project.asset_kit || {};
  const direction = (project.directions || []).find((d) => d.id === project.selected_direction_id);
  const [surface, setSurface] = useState('flyer');
  const [savingToCampaign, setSavingToCampaign] = useState(false);
  const previewRef = useRef(null);

  const downloadPng = async () => {
    if (!previewRef.current) return;
    try {
      const canvas = await html2canvas(previewRef.current, { scale: 2, backgroundColor: null, useCORS: true });
      const link = document.createElement('a');
      link.download = `${(ak.copy?.headline || 'creative').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${surface}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('PNG downloaded.');
    } catch {
      toast.error('Could not export.');
    }
  };

  const saveToCampaign = async () => {
    if (savingToCampaign) return;
    setSavingToCampaign(true);
    try {
      const { data } = await axios.post(`${API}/api/creative-studio/save-to-campaign`, {
        project_id: project.id,
      });
      if (data?.ok) {
        toast.success(`Saved ${data.assets_added} assets to "${data.campaign_name}".`);
      } else {
        toast.error(data?.detail || 'Could not save.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No active campaign — launch one first.';
      toast.error(msg);
    } finally {
      setSavingToCampaign(false);
    }
  };

  if (!direction) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="creative-assets-step">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Pill>{direction.name}</Pill>
            <span className="text-[11px]" style={{ color: 'rgba(245,240,232,0.5)' }}>
              Feels like {(direction.feels_like || []).join(' · ')}
            </span>
          </div>
          <h2 className="text-3xl" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>
            Your campaign creative is ready.
          </h2>
        </div>
        <button onClick={onReset} data-testid="creative-restart-btn"
          className="text-[12px] font-semibold" style={{ color: 'rgba(245,240,232,0.5)' }}>
          Start a new one →
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-5">
        {SURFACES.map((s) => <SurfaceTab key={s.id} {...s} active={surface === s.id} onClick={setSurface} />)}
      </div>

      <div className="grid lg:grid-cols-[1.05fr,1fr] gap-6 items-start">
        <div>
          <div className="rounded-3xl overflow-hidden"
            style={{ background: direction.visual_spec?.background || '#0a0a0a', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div ref={previewRef} style={{ width: '100%' }}>
              <CreativePreview surface={surface} direction={direction} copy={ak.copy} surfaceCopy={ak.surfaces?.[surface]} />
            </div>
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={downloadPng} data-testid="creative-download-png"
              className="flex-1 px-4 py-2.5 rounded-full text-[12px] font-bold flex items-center justify-center gap-2 transition hover:opacity-90"
              style={{ background: LIME, color: '#0a0a0a' }}>
              <DownloadSimple size={14} weight="fill" /> Download PNG
            </button>
            <button onClick={saveToCampaign} disabled={savingToCampaign} data-testid="creative-save-campaign-btn"
              className="px-4 py-2.5 rounded-full text-[12px] font-bold flex items-center justify-center gap-2 transition"
              style={{ background: 'rgba(200,240,96,0.08)', color: LIME, border: '1px solid rgba(200,240,96,0.2)' }}>
              <BookmarkSimple size={14} weight="fill" /> {savingToCampaign ? 'Saving…' : 'Save to campaign'}
            </button>
            <button onClick={() => navigator.clipboard.writeText(ak.surfaces?.[surface]?.headline || ak.copy?.headline || '')}
              className="px-4 py-2.5 rounded-full text-[12px] font-semibold"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.85)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CopyIcon size={12} className="inline mr-1" /> Copy text
            </button>
          </div>
        </div>

        <div className="rounded-3xl p-6" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)' }}>
          <CopyPanel copy={ak.copy || {}} />
          {ak.why_this_works && (
            <div className="mt-6 pt-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} data-testid="creative-why-block">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb size={14} weight="fill" style={{ color: LIME }} />
                <p className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: LIME }}>Why this creative works</p>
              </div>
              <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.78)' }}>{ak.why_this_works}</p>
            </div>
          )}
        </div>
      </div>

      <CarouselGallery slides={ak.carousel?.slides} direction={direction} />
    </motion.div>
  );
}
